// ClientsManagement.tsx

import { useRef, useState } from 'react';
import Pagination from '../components/Pagination.tsx';
import { Contact, useOrganizations } from '../hooks/useOrganizations.ts'; 
import Spinner from '../components/Spinner.tsx';
import Modal from '../components/Modal.tsx';
import { MdOutlineDeleteOutline } from 'react-icons/md';
import more from '../Images/more-vertical.svg';
import useOnClickOutside from '../hooks/useOnClickOutside.ts';
import user_plus from '../Images/user-plus2.svg';
import { CiEdit } from 'react-icons/ci';
import { BsThreeDotsVertical } from 'react-icons/bs';
import styles from './UsersManagement.module.css';
import { useUsers } from '../hooks/useUsers.ts'; // Import useUsers hook
import { useTheme } from '../hooks/useTheme.ts';

const getData = function (organizations: Contact[] | undefined, page: number, limit: number) {
  if (organizations === undefined) {
    return [];
  }
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  return organizations.slice(startIndex, endIndex);
};

function ClientsManagement() {
  const { isLoading, allOrganizations, error } = useOrganizations();
  const { isLoading: isLoadingUsers, allUsers, error: errorUsers } = useUsers(); // Fetch all users
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';
  
  const [currentPage, setCurrentPage] = useState(1);
  const limitOfOrgs = 7;

  const [showActionsModal, setShowActionsModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Contact | null>(null);
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openRemoveUserModal, setOpenRemoveUserModal] = useState(false);
  const [openEditOrgModal, setOpenEditOrgModal] = useState(false);
  const [openConfirmDeleteOrgModal, setOpenConfirmDeleteOrgModal] = useState(false);
  
  const popUpMenuRef = useRef<HTMLDivElement>(null);

  const handleAddUser = (org: Contact) => {
    setSelectedOrg(org);
    setOpenAddUserModal(true);
    setShowActionsModal(false);
  };

  const handleRemoveUser = (org: Contact) => {
    setSelectedOrg(org);
    setOpenRemoveUserModal(true);
    setShowActionsModal(false);
  };

  const handleEditOrg = (org: Contact) => {
    setSelectedOrg(org);
    setOpenEditOrgModal(true);
    setShowActionsModal(false);
  };

  const handleDeleteOrg = (org: Contact) => {
    setSelectedOrg(org);
    setOpenConfirmDeleteOrgModal(true);
    setShowActionsModal(false);
  };

  const handleActionsClick = (org: Contact) => {
    setSelectedOrg(org);
    setShowActionsModal(!showActionsModal);
  };

  const clickOutSideHandler = () => {
    setShowActionsModal(false);
  };

  // @ts-ignore - Ignoring type issue with useOnClickOutside
  useOnClickOutside(popUpMenuRef, clickOutSideHandler);

  const renderOrganizations = () => {
    return getData(allOrganizations, currentPage, limitOfOrgs).map((org) => {
      return (
        <tr key={org.id}>
          <td>
            <div className={styles.avatar}>
              {org.name}
            </div>
          </td>
          <td>{org.email}</td>
          <td>{org.phone}</td>
          <td>{org.address}</td>
          <td className={styles.role_info}>
            <div className={styles.more} onClick={() => handleActionsClick(org)}>
              {isDarkTheme ? (
                <BsThreeDotsVertical 
                  size={20} 
                  color="#FFFFFF" 
                  style={{ 
                    filter: 'drop-shadow(0px 0px 1px rgba(255, 255, 255, 0.5))',
                    opacity: 0.9
                  }} 
                />
              ) : (
                <img src={more} alt="More Options" />
              )}
              {selectedOrg?.id === org.id && showActionsModal && (
                <div className={styles.popOut} ref={popUpMenuRef}>
                  <ul className={styles.list}>
                    <li>
                      <button
                        className={styles.list_button}
                        onClick={() => handleAddUser(org)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          <img src={user_plus} alt="Add User" className={styles.user_img} />
                          Add User
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        className={styles.list_button}
                        onClick={() => handleRemoveUser(org)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            color: '#D14D41',
                          }}
                        >
                          <MdOutlineDeleteOutline
                            style={{
                              fontSize: '16px',
                            }}
                          />
                          Remove User
                        </div>
                      </button>
                    </li>
                    <li
                      style={{
                        borderTop: '1px solid #E2E6EA',
                      }}
                    >
                      <button
                        className={styles.list_button}
                        onClick={() => handleEditOrg(org)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          <CiEdit
                            style={{
                              fontSize: '16px',
                            }}
                          />
                          Edit Organization
                        </div>
                      </button>
                    </li>
                    <li>
                      <button
                        className={styles.list_button}
                        onClick={() => handleDeleteOrg(org)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            color: '#D14D41',
                          }}
                        >
                          <MdOutlineDeleteOutline
                            style={{
                              fontSize: '16px',
                            }}
                          />
                          Delete Organization
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      {(isLoading || isLoadingUsers) && <Spinner />}
      
      {/* Display Organizations Table */}
      {allOrganizations && allUsers && !isLoading && !isLoadingUsers && !error && !errorUsers && (
        <>
          <div className={styles.usersTable}>
            <div className={styles.tableContainer}>
              <table>
                <thead>
                  <tr>
                    <th>
                      <span style={{ marginRight: 10 }}>Name</span>
                    </th>
                    <th>
                      <span>Email</span>
                    </th>
                    <th>
                      <span>Phone Number</span>
                    </th>
                    <th>
                      <span>Address</span>
                    </th>
                    <th>
                      <span>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>{renderOrganizations()}</tbody>
              </table>
            </div>
          </div>
          
          {allOrganizations.length > limitOfOrgs && (
            <Pagination
              currentPage={currentPage}
              lastPage={Math.ceil(allOrganizations.length / limitOfOrgs)}
              maxLength={7}
              setCurrentPage={setCurrentPage}
            />
          )}
        </>
      )}
      
      {openAddUserModal && selectedOrg && (
        // @ts-ignore - Type mismatch between Modal's User interface and useUsers's User interface
        <Modal
          open={openAddUserModal}
          setOpen={setOpenAddUserModal}
          addUserToOrganization={true}
          orgId={selectedOrg.id.toString()}
          organization={selectedOrg.name}
          allUsers={allUsers}
        />
      )}
      
      {openRemoveUserModal && selectedOrg && (
        // @ts-ignore - Type mismatch between Modal's User interface and useUsers's User interface
        <Modal
          open={openRemoveUserModal}
          setOpen={setOpenRemoveUserModal}
          confirmOrganizationUser={true}
          orgId={selectedOrg.id.toString()}
          organization={selectedOrg.name}
          allUsers={allUsers}
        />
      )}
      
      {openEditOrgModal && selectedOrg && (
        // @ts-ignore - Type issues with Modal component
        <Modal
          open={openEditOrgModal}
          setOpen={setOpenEditOrgModal}
          editOrg={true}
          orgId={selectedOrg.id.toString()}
        />
      )}
      
      {openConfirmDeleteOrgModal && selectedOrg && (
        // @ts-ignore - Type issues with Modal component
        <Modal
          open={openConfirmDeleteOrgModal}
          setOpen={setOpenConfirmDeleteOrgModal}
          confirmOrganization={true}
          orgId={selectedOrg.id.toString()}
        />
      )}
      
      {error && <p>An error occurred: {error.message}</p>}
      {errorUsers && <p>An error occurred while fetching users: {errorUsers.message}</p>}
    </>
  );
}

export default ClientsManagement;
